import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  FileText,
  Plus,
  BarChart3,
  MessageSquare,
  Settings,
  Command,
} from "lucide-react";
import { fetchWithAuth } from "@/lib/api/client";
import type { Licitacion } from "@/types/licitacion";

interface QuickAction {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  href: string;
}

interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  href: string;
}

const quickActions: QuickAction[] = [
  {
    id: "nueva-licitacion",
    title: "Nueva Licitacion",
    subtitle: "Crear una nueva licitacion",
    icon: <Plus className="h-4 w-4" />,
    href: "/licitaciones/nueva",
  },
  {
    id: "dashboard",
    title: "Dashboard",
    subtitle: "Ir al panel principal",
    icon: <BarChart3 className="h-4 w-4" />,
    href: "/",
  },
  {
    id: "analytics",
    title: "Analytics",
    subtitle: "Ver estadisticas y metricas",
    icon: <BarChart3 className="h-4 w-4" />,
    href: "/analytics",
  },
  {
    id: "chat-ia",
    title: "Chat IA",
    subtitle: "Conversar con el asistente IA",
    icon: <MessageSquare className="h-4 w-4" />,
    href: "/chat",
  },
  {
    id: "configuracion",
    title: "Configuracion",
    subtitle: "Ajustes del modelo LLM",
    icon: <Settings className="h-4 w-4" />,
    href: "/configuracion/llm",
  },
];

export default function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [licitaciones, setLicitaciones] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const routerNavigate = useNavigate();

  // Open/close with Cmd+K / Ctrl+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      setLicitaciones([]);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim()) {
      setLicitaciones([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const data = await fetchWithAuth(
          `/api/v1/licitaciones?search=${encodeURIComponent(query.trim())}&page_size=5`
        );
        const results: SearchResult[] = (data.data as Licitacion[]).map(
          (lic) => ({
            id: lic.id,
            title: lic.titulo,
            subtitle: lic.organismo,
            icon: <FileText className="h-4 w-4" />,
            href: `/licitaciones/${lic.id}`,
          })
        );
        setLicitaciones(results);
      } catch {
        setLicitaciones([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Build the flat list of all items for keyboard navigation
  const filteredActions = query.trim()
    ? quickActions.filter(
        (a) =>
          a.title.toLowerCase().includes(query.toLowerCase()) ||
          a.subtitle.toLowerCase().includes(query.toLowerCase())
      )
    : quickActions;

  const allItems: SearchResult[] = [...licitaciones, ...filteredActions];

  // Reset selected index when items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [licitaciones.length, filteredActions.length]);

  const navigate = useCallback(
    (href: string) => {
      setIsOpen(false);
      routerNavigate(href);
    },
    [routerNavigate]
  );

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < allItems.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : allItems.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (allItems[selectedIndex]) {
            navigate(allItems[selectedIndex].href);
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, allItems, selectedIndex, navigate]);

  if (!isOpen) return null;

  const hasLicitaciones = licitaciones.length > 0;
  const hasActions = filteredActions.length > 0;
  const hasResults = hasLicitaciones || hasActions;
  const showEmpty = query.trim() && !isLoading && !hasResults;

  // Track offset for indexing: licitaciones first, then actions
  let itemIndex = 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="mx-auto mt-[20vh] max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
          <Search className="h-5 w-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar licitaciones, acciones..."
            className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none"
          />
          <kbd className="hidden items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-400 sm:flex">
            <Command className="h-3 w-3" />K
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto p-2">
          {/* Loading state */}
          {isLoading && (
            <div className="space-y-2 p-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-8 w-8 animate-pulse rounded-lg bg-gray-100" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100" />
                    <div className="h-2 w-1/2 animate-pulse rounded bg-gray-100" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Licitaciones results */}
          {!isLoading && hasLicitaciones && (
            <div className="mb-2">
              <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Licitaciones
              </p>
              {licitaciones.map((item) => {
                const currentIndex = itemIndex++;
                return (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.href)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      selectedIndex === currentIndex
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                        selectedIndex === currentIndex
                          ? "bg-blue-100 text-blue-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{item.title}</p>
                      <p className="truncate text-xs text-gray-400">
                        {item.subtitle}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Quick actions */}
          {!isLoading && hasActions && (
            <div>
              <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Acciones rapidas
              </p>
              {filteredActions.map((action) => {
                const currentIndex = itemIndex++;
                return (
                  <button
                    key={action.id}
                    onClick={() => navigate(action.href)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      selectedIndex === currentIndex
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                        selectedIndex === currentIndex
                          ? "bg-blue-100 text-blue-600"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {action.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{action.title}</p>
                      <p className="truncate text-xs text-gray-400">
                        {action.subtitle}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {showEmpty && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              No se encontraron resultados
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
